/**
 * Webhook payload builder + preview (ENG-2427 items 2–3).
 *
 * A site webhook's `message_body` is a JSON template. queue_consumer
 * (`consumers/webhook/webhook_consumer.py`) renders it once per delivered
 * event with Python `str.format_map`, so this module mirrors those rules in
 * the browser:
 *
 * - every string value is rendered; keys are not
 * - a placeholder the event doesn't carry renders `"..."`
 * - everything renders as a string, except a standalone `"{bounding_boxes}"`
 *   (native array) and `"{alert_images}"` (native array of frames)
 * - `{{` / `}}` are literal braces
 * - a value that fails to format (stray `{`, `{}`) ships unrendered
 * - detection with a body of exactly `{}` sends no body at all
 * - alert_mp4 with an empty body gets the consumer's default payload
 *
 * The three event types delivered today are detection, alert_end and
 * alert_mp4; each carries a different field set (see WEBHOOK_FIELDS).
 */

export type WebhookEventValue = "detection" | "alert_end" | "alert_mp4";

export const DELIVERED_EVENT_TYPES: WebhookEventValue[] = [
  "detection",
  "alert_end",
  "alert_mp4",
];

export const EVENT_TYPE_LABEL: Record<string, string> = {
  detection: "Detection",
  alert_end: "Alert end",
  alert_mp4: "Alert clip (MP4)",
  no_motion: "No motion",
  unable_to_connect: "Unable to connect",
};

export const EVENT_TYPE_HINT: Record<string, string> = {
  detection: "Sent the moment an alert fires, with the triggering boxes.",
  alert_end:
    "Sent when the alert window closes, with every box seen in the window.",
  alert_mp4:
    "Sent once the alert's MP4 clip is ready (up to 4 minutes after it ends), with a download link.",
  no_motion: "Not delivered today.",
  unable_to_connect: "Not delivered today.",
};

export interface WebhookField {
  /** The placeholder name, as written between braces. */
  key: string;
  /** Customer-facing name. */
  label: string;
  description: string;
  /** Events that carry the field; any other event renders "...". */
  events: WebhookEventValue[];
  /** Renders as a native JSON value when it is a value on its own. */
  native?: boolean;
  /** Kept working for existing templates; not offered in the builder. */
  legacy?: boolean;
}

const ALL: WebhookEventValue[] = ["detection", "alert_end", "alert_mp4"];

export const WEBHOOK_FIELDS: WebhookField[] = [
  {
    key: "site_id",
    label: "Site ID",
    description: "The site's unique numeric ID.",
    events: ALL,
  },
  {
    key: "display_name",
    label: "Site name",
    description: "The site's name as shown in Actuate.",
    events: ALL,
  },
  {
    key: "camera_id",
    label: "Camera ID",
    description: "The camera's unique numeric ID.",
    events: ["detection"],
  },
  {
    key: "camera_name",
    label: "Camera name",
    description: "The camera's name as shown in Actuate.",
    events: ALL,
  },
  {
    key: "camera_guid",
    label: "Camera GUID",
    description: "The camera's ID in the VMS, when the VMS has one.",
    events: ["detection"],
  },
  {
    key: "event_type",
    label: "Event type",
    description: "detection, alert_end or alert_mp4.",
    events: ALL,
  },
  {
    key: "event_id",
    label: "Event ID",
    description:
      "Opaque ID shared by every event of one alert, to match them up.",
    events: ALL,
  },
  {
    key: "label",
    label: "Detected object",
    description: "What was detected, for example intruder or vehicle.",
    events: ALL,
  },
  {
    key: "confidence",
    label: "Confidence",
    description: "Detection confidence, 0–100.",
    events: ["detection", "alert_end"],
  },
  {
    key: "time",
    label: "Time",
    description: "Readable time in the site's timezone.",
    events: ALL,
  },
  {
    key: "event_timestamp_ms",
    label: "Timestamp (ms)",
    description: "Unix time in milliseconds.",
    events: ALL,
  },
  {
    key: "alert_url",
    label: "Alert link",
    description: "Link to the alert in Actuate.",
    events: ["detection"],
  },
  {
    key: "message",
    label: "Alert text",
    description: "A short readable summary of the alert.",
    events: ["detection"],
  },
  {
    key: "bounding_boxes",
    label: "Bounding boxes",
    description:
      "Array of boxes ({label, confidence, x, y, width, height}) in frame pixels. Empty on alert_mp4.",
    events: ["detection", "alert_end"],
    native: true,
  },
  {
    key: "frame_width",
    label: "Frame width",
    description: "Width in pixels of the frame the boxes refer to.",
    events: ["detection", "alert_end"],
  },
  {
    key: "frame_height",
    label: "Frame height",
    description: "Height in pixels of the frame the boxes refer to.",
    events: ["detection", "alert_end"],
  },
  {
    key: "alert_image",
    label: "Alert image",
    description: "The alert frame as a base64 JPEG string.",
    events: ["detection"],
  },
  {
    key: "alert_images",
    label: "Alert images",
    description: "Array of the 3 most recent frames as base64 JPEGs.",
    events: ["detection"],
    native: true,
  },
  {
    key: "window_id",
    label: "Alert window ID",
    description: "Internal ID of the alert window.",
    events: ["alert_end", "alert_mp4"],
  },
  {
    key: "start_time",
    label: "Window start",
    description: "Unix time in seconds the alert window opened.",
    events: ["alert_end", "alert_mp4"],
  },
  {
    key: "event_timestamp",
    label: "Window end",
    description: "Unix time in seconds the alert window closed.",
    events: ["alert_end", "alert_mp4"],
  },
  {
    key: "mp4_url",
    label: "Clip URL",
    description: "Temporary download link for the alert's MP4 clip.",
    events: ["alert_mp4"],
  },
  {
    key: "mp4_url_expires_in_s",
    label: "Clip URL lifetime",
    description: "Seconds until the clip link expires.",
    events: ["alert_mp4"],
  },
  {
    key: "stream_id",
    label: "Stream ID",
    description: "Internal ID of the camera stream.",
    events: ALL,
  },
  {
    key: "site_name",
    label: "Site connector name",
    description: "Internal name (connector-<id>). Use Site name instead.",
    events: ALL,
    legacy: true,
  },
  {
    key: "message_group_id",
    label: "Site ID (old name)",
    description: "Same value as Site ID. Use Site ID instead.",
    events: ALL,
    legacy: true,
  },
  {
    key: "custcam_id",
    label: "Camera feature key",
    description: "Internal key.",
    events: ["alert_end", "alert_mp4"],
    legacy: true,
  },
  {
    key: "s3_folder",
    label: "Window key",
    description: "Internal key.",
    events: ["alert_end", "alert_mp4"],
    legacy: true,
  },
];

export const WEBHOOK_FIELD_BY_KEY: Record<string, WebhookField> =
  Object.fromEntries(WEBHOOK_FIELDS.map((field) => [field.key, field]));

/** Fields the builder offers (legacy aliases still work in Advanced). */
export const BUILDER_FIELDS = WEBHOOK_FIELDS.filter((field) => !field.legacy);

// --- Sample events -------------------------------------------------------

const SAMPLE_EVENT_ID = "6f1c2d8e-4b7a-5e21-9c3d-2a8f0b6e7d41";
const SAMPLE_IMAGE = "/9j/4AAQSkZJRgABAQ…(base64 JPEG)";
const SAMPLE_BOXES = [
  {
    label: "intruder",
    confidence: 0.97,
    x: 860,
    y: 420,
    width: 200,
    height: 500,
  },
];
const SAMPLE_WINDOW_BOXES = [
  { ...SAMPLE_BOXES[0], timestamp: 1753362832.73 },
  {
    label: "intruder",
    confidence: 0.94,
    x: 902,
    y: 431,
    width: 196,
    height: 488,
    timestamp: 1753362833.24,
  },
];

const ALERT_END_EVENT: Record<string, unknown> = {
  event_type: "alert_end",
  message_group_id: 35382,
  stream_id: 476536,
  custcam_id: "productionconnector-35382Loading_Dock",
  window_id: "productionconnector-35382Loading_Dockintruder1753362832.7313843",
  s3_folder: "1753362832.7313843",
  label: "intruder",
  site_name: "connector-35382",
  display_name: "Main Street Depot",
  camera_name: "Loading Dock",
  start_time: 1753362832.7313843,
  event_timestamp: 1753362861.25,
  time: "07/24/25, 8:14 AM CDT",
};

/**
 * What each event carries when the consumer renders it, including the fields
 * the consumer adds (site_id, event_id, and the alert_end / alert_mp4
 * derived values). Secret-named keys are left out: the consumer drops them.
 */
export const SAMPLE_EVENTS: Record<
  WebhookEventValue,
  Record<string, unknown>
> = {
  detection: {
    event_type: "detection",
    message_group_id: 35382,
    site_id: 35382,
    stream_id: 476536,
    label: "intruder",
    site_name: "connector-35382",
    display_name: "Main Street Depot",
    camera_name: "Loading Dock",
    confidence: 97,
    alert_url:
      "https://www.actuateui.net/?custcam_id=productionconnector-35382Loading_Dockthreatperson&s3_folder=1753362832.7313843&alert_label=intruder",
    time: "07/24/25, 8:13 AM CDT",
    message:
      "Detection: intruder\n                Camera: Loading Dock\n                Site Name: Main Street Depot\n                Time: 07/24/25, 8:13 AM CDT\n                Click Here To View: https://www.actuateui.net/?…",
    camera_id: 399317,
    username: "(camera username)",
    camera_guid: "",
    frame_width: 1920,
    frame_height: 1080,
    bounding_boxes: SAMPLE_BOXES,
    event_timestamp_ms: 1753362832731,
    queue_id: "event_queue_webhook.fifo",
    event_id: SAMPLE_EVENT_ID,
  },
  alert_end: {
    ...ALERT_END_EVENT,
    site_id: 35382,
    event_id: SAMPLE_EVENT_ID,
    frame_width: 1920,
    frame_height: 1080,
    event_timestamp_ms: 1753362861250,
    confidence: 97,
    bounding_boxes: SAMPLE_WINDOW_BOXES,
  },
  alert_mp4: {
    ...ALERT_END_EVENT,
    site_id: 35382,
    event_id: SAMPLE_EVENT_ID,
    event_type: "alert_mp4",
    mp4_url:
      "https://alert-clips.s3.amazonaws.com/…/alert.mp4?X-Amz-Expires=3600&X-Amz-Signature=…",
    mp4_url_expires_in_s: 3600,
    event_timestamp_ms: 1753362861250,
    bounding_boxes: [],
  },
};

const MP4_DEFAULT_BODY = {
  event_type: "alert_mp4",
  event_id: "{event_id}",
  window_id: "{window_id}",
  label: "{label}",
  camera_name: "{camera_name}",
  site_name: "{site_name}",
  time: "{time}",
  mp4_url: "{mp4_url}",
  mp4_url_expires_in_s: 3600,
};

// --- Python str.format_map ------------------------------------------------

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** Python str() of a JSON-ish value. */
export const pyStr = (value: unknown): string => {
  if (typeof value === "string") return value;
  return pyRepr(value);
};

const pyRepr = (value: unknown): string => {
  if (value === null || value === undefined) return "None";
  if (value === true) return "True";
  if (value === false) return "False";
  if (typeof value === "number") return String(value);
  if (typeof value === "string") return `'${value.replace(/'/g, "\\'")}'`;
  if (Array.isArray(value)) return `[${value.map(pyRepr).join(", ")}]`;
  if (isPlainObject(value)) {
    return `{${Object.entries(value)
      .map(([key, item]) => `${pyRepr(key)}: ${pyRepr(item)}`)
      .join(", ")}}`;
  }
  return String(value);
};

export class TemplateFormatError extends Error {}

/**
 * Python `template.format_map(context)` where a missing key renders
 * `fallback`. Only plain `{name}` fields are supported; a conversion, format
 * spec, attribute or index makes the preview give up on that value
 * (TemplateFormatError) rather than guess.
 */
export const formatMap = (
  template: string,
  context: Record<string, unknown>,
  fallback = "..."
): string => {
  let out = "";
  let i = 0;
  while (i < template.length) {
    const ch = template[i];
    if (ch === "{") {
      if (template[i + 1] === "{") {
        out += "{";
        i += 2;
        continue;
      }
      const close = template.indexOf("}", i + 1);
      if (close === -1) {
        throw new TemplateFormatError(
          "Single '{' encountered in format string"
        );
      }
      const field = template.slice(i + 1, close);
      if (field === "" || /^\d+$/.test(field)) {
        throw new TemplateFormatError(`Positional placeholder {${field}}`);
      }
      if (/[{!:.[]/.test(field)) {
        throw new TemplateFormatError(
          `Placeholder {${field}} uses formatting the preview can't show`
        );
      }
      out += field in context ? pyStr(context[field]) : fallback;
      i = close + 1;
      continue;
    }
    if (ch === "}") {
      if (template[i + 1] === "}") {
        out += "}";
        i += 2;
        continue;
      }
      throw new TemplateFormatError("Single '}' encountered in format string");
    }
    out += ch;
    i += 1;
  }
  return out;
};

/** Placeholder names in one string (`{{`/`}}` escapes skipped). */
export const placeholdersInString = (value: string): string[] => {
  const names: string[] = [];
  const pattern = /\{\{|\}\}|\{([^{}]*)\}/g;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(value)) !== null) {
    if (match[1] !== undefined) names.push(match[1]);
  }
  return names;
};

const walkStrings = (value: unknown, visit: (text: string) => void) => {
  if (typeof value === "string") visit(value);
  else if (Array.isArray(value))
    value.forEach((item) => walkStrings(item, visit));
  else if (isPlainObject(value))
    Object.values(value).forEach((item) => walkStrings(item, visit));
};

/** Every placeholder used anywhere in a parsed template's values. */
export const placeholdersInTemplate = (template: unknown): string[] => {
  const names = new Set<string>();
  walkStrings(template, (text) =>
    placeholdersInString(text).forEach((name) => names.add(name))
  );
  return Array.from(names);
};

// --- Rendering -----------------------------------------------------------

export interface RenderResult {
  /** The JSON the receiver gets; null when no body is sent. */
  body: unknown;
  /** Why nothing is delivered at all, when that's the case. */
  error?: string;
  /** Things that render differently from what the author likely meant. */
  notes: string[];
}

const BBOX_SENTINEL = "__actuate_bounding_boxes__";

const replaceSentinel = (obj: unknown, sentinel: string, value: unknown) => {
  if (Array.isArray(obj)) {
    obj.forEach((item, index) => {
      if (item === sentinel) obj[index] = value;
      else replaceSentinel(item, sentinel, value);
    });
  } else if (isPlainObject(obj)) {
    Object.entries(obj).forEach(([key, item]) => {
      if (item === sentinel) obj[key] = value;
      else replaceSentinel(item, sentinel, value);
    });
  }
};

const formatStringValue = (
  value: string,
  context: Record<string, unknown>,
  eventType: WebhookEventValue,
  notes: string[]
): unknown => {
  if (value.includes("{alert_images}")) {
    // The whole value becomes the frame list (alert_end / alert_mp4 have none).
    return eventType === "detection"
      ? [SAMPLE_IMAGE, SAMPLE_IMAGE, SAMPLE_IMAGE]
      : [];
  }
  if (value.includes("{alert_image}")) {
    if (eventType !== "detection") {
      notes.push(
        `{alert_image} renders empty on ${eventType}: it has no image.`
      );
      return value.split("{alert_image}").join("");
    }
    return value.split("{alert_image}").join(SAMPLE_IMAGE);
  }
  try {
    return formatMap(value, context);
  } catch (error) {
    notes.push(`"${value}" is sent unrendered: ${(error as Error).message}.`);
    return value;
  }
};

const formatMessage = (
  message: Record<string, unknown>,
  context: Record<string, unknown>,
  eventType: WebhookEventValue,
  notes: string[]
) => {
  Object.entries(message).forEach(([key, value]) => {
    if (isPlainObject(value)) {
      formatMessage(value, context, eventType, notes);
    } else if (Array.isArray(value)) {
      value.forEach((item, index) => {
        if (isPlainObject(item)) formatMessage(item, context, eventType, notes);
        else if (typeof item === "string")
          value[index] = formatStringValue(item, context, eventType, notes);
      });
    } else if (typeof value === "string") {
      message[key] = formatStringValue(value, context, eventType, notes);
    }
  });
  delete message.max_image_size;
};

/** Detection: merge, or on an unknown key fall back to a shallow overwrite. */
const mergeCustomFieldsDetection = (
  message: Record<string, unknown>,
  customFields: Record<string, unknown>
) => {
  for (const [key, value] of Object.entries(customFields)) {
    if (!(key in message)) {
      Object.assign(message, customFields);
      return;
    }
    const current = message[key];
    if (isPlainObject(current)) {
      if (!isPlainObject(value)) return; // dict.update(non-dict) raises; stops
      Object.assign(current, value);
    } else {
      message[key] = value;
    }
  }
};

/** alert_end / alert_mp4: merge dicts, otherwise set. */
const mergeCustomFieldsWindow = (
  message: Record<string, unknown>,
  customFields: Record<string, unknown>
) => {
  Object.entries(customFields).forEach(([key, value]) => {
    const current = message[key];
    if (isPlainObject(current) && isPlainObject(value)) {
      Object.assign(current, value);
    } else {
      message[key] = value;
    }
  });
};

const parseTemplate = (
  messageBody: string | null | undefined
): { message?: Record<string, unknown>; error?: string } => {
  try {
    const parsed = JSON.parse(messageBody || "{}");
    if (!isPlainObject(parsed)) {
      return { error: "The message body must be a JSON object ({ … })." };
    }
    return { message: parsed };
  } catch {
    return {
      error:
        "The message body isn't valid JSON, so the webhook drops every event.",
    };
  }
};

/**
 * Render a site webhook's template against the sample event, the way the
 * consumer does. `customFields` are a camera link's per-stream fields.
 */
export const renderWebhookPayload = ({
  messageBody,
  eventType,
  customFields = {},
  targetUrl = "",
  event = SAMPLE_EVENTS[eventType],
}: {
  messageBody: string | null | undefined;
  eventType: WebhookEventValue;
  customFields?: Record<string, unknown> | null;
  targetUrl?: string;
  event?: Record<string, unknown>;
}): RenderResult => {
  const notes: string[] = [];
  const parsed = parseTemplate(messageBody);
  if (!parsed.message) return { body: null, error: parsed.error, notes };
  let message = parsed.message;
  const fields = customFields || {};

  const context: Record<string, unknown> = { ...event };
  const boxes = event.bounding_boxes;

  if (eventType === "detection") {
    const emptyBody = messageBody === "{}";
    mergeCustomFieldsDetection(message, fields);
    if (Array.isArray(boxes)) context.bounding_boxes = BBOX_SENTINEL;
    formatMessage(message, context, eventType, notes);
    if (Array.isArray(boxes)) replaceSentinel(message, BBOX_SENTINEL, boxes);
    if (targetUrl.includes("slack") && !("text" in message)) {
      message.text = event.message;
    }
    if (emptyBody) {
      return {
        body: null,
        notes: [
          "A body of {} sends the request with no body on detection events.",
        ],
      };
    }
  } else {
    if (eventType === "alert_mp4" && Object.keys(message).length === 0) {
      message = JSON.parse(JSON.stringify(MP4_DEFAULT_BODY));
      notes.push("An empty body gets the default clip payload on alert_mp4.");
    }
    mergeCustomFieldsWindow(message, fields);
    replaceSentinel(message, "{alert_images}", []);
    context.bounding_boxes = BBOX_SENTINEL;
    formatMessage(message, context, eventType, notes);
    replaceSentinel(
      message,
      BBOX_SENTINEL,
      eventType === "alert_end" && Array.isArray(boxes) ? boxes : []
    );
  }

  return { body: message, notes };
};

// --- Warnings for the template author --------------------------------------

export interface TemplateWarning {
  placeholder: string;
  message: string;
}

/**
 * Placeholders that render "..." for one of the chosen events: unknown names
 * and fields the event doesn't carry.
 */
export const templateWarnings = (
  messageBody: string,
  eventTypes: string[]
): TemplateWarning[] => {
  const parsed = parseTemplate(messageBody);
  if (!parsed.message) return [];
  const delivered = eventTypes.filter((value): value is WebhookEventValue =>
    (DELIVERED_EVENT_TYPES as string[]).includes(value)
  );
  const warnings: TemplateWarning[] = [];
  placeholdersInTemplate(parsed.message).forEach((name) => {
    if (name === "" || /[!:.[]/.test(name)) return;
    const field = WEBHOOK_FIELD_BY_KEY[name];
    if (!field) {
      warnings.push({
        placeholder: name,
        message: `{${name}} isn't a webhook field; it always sends "...".`,
      });
      return;
    }
    const missing = delivered.filter((event) => !field.events.includes(event));
    if (missing.length > 0) {
      const sendsEmptyArray =
        field.native && missing.every((event) => event !== "detection");
      warnings.push({
        placeholder: name,
        message: sendsEmptyArray
          ? `{${name}} is an empty array on ${missing.join(", ")}.`
          : `{${name}} sends "..." on ${missing.join(", ")}.`,
      });
    }
  });
  return warnings;
};

// --- Builder ---------------------------------------------------------------

export interface BuilderRow {
  /** Placeholder name. */
  field: string;
  /** Key in the JSON the receiver gets. */
  key: string;
}

/** Builder rows -> the message_body template text. */
export const buildTemplate = (rows: BuilderRow[]): string => {
  const template: Record<string, string> = {};
  rows.forEach((row) => {
    const key = row.key.trim() || row.field;
    template[key] = `{${row.field}}`;
  });
  return JSON.stringify(template, null, 2);
};

/**
 * The builder rows a template came from, or null when it holds anything the
 * builder can't express (nesting, fixed text, unknown or repeated fields).
 * An empty template is an empty builder.
 */
export const parseBuilderRows = (
  messageBody: string | null | undefined
): BuilderRow[] | null => {
  if (!messageBody || !messageBody.trim()) return [];
  const parsed = parseTemplate(messageBody);
  if (!parsed.message) return null;
  const rows: BuilderRow[] = [];
  const seen = new Set<string>();
  for (const [key, value] of Object.entries(parsed.message)) {
    if (typeof value !== "string") return null;
    const match = /^\{([a-z0-9_]+)\}$/.exec(value);
    if (!match) return null;
    const field = WEBHOOK_FIELD_BY_KEY[match[1]];
    if (!field || field.legacy || seen.has(field.key)) return null;
    seen.add(field.key);
    rows.push({ field: field.key, key });
  }
  return rows;
};
